import { useState } from 'react';

const categories = ['All', 'Framing', 'Concrete', 'Remodeling'];

const projects = [
  {
    title: 'Luxury Home Framing',
    location: 'Southlake, TX',
    category: 'Framing',
    year: '2024',
    image: 'https://public.readdy.ai/ai/img_res/ac05f6874a23c842ae6a9ad298d07431.jpg',
  },
  {
    title: 'Commercial Concrete Foundation',
    location: 'Fort Worth, TX',
    category: 'Concrete',
    year: '2024',
    image: 'https://readdy.ai/api/search-image?query=large%20commercial%20concrete%20foundation%20being%20poured%20in%20Fort%20Worth%20Texas%2C%20construction%20workers%20finishing%20concrete%20slab%2C%20professional%20photography%2C%20warm%20afternoon%20light%2C%20industrial%20construction%20site&width=600&height=450&seq=proj002&orientation=landscape',
  },
  {
    title: 'Full Kitchen Renovation',
    location: 'Plano, TX',
    category: 'Remodeling',
    year: '2024',
    image: 'https://readdy.ai/api/search-image?query=stunning%20modern%20kitchen%20renovation%20completed%20in%20Plano%20Texas%20home%2C%20white%20shaker%20cabinets%2C%20quartz%20countertops%2C%20stainless%20appliances%2C%20open%20concept%20design%2C%20bright%20natural%20light%2C%20professional%20interior%20photography&width=600&height=450&seq=proj003&orientation=landscape',
  },
  {
    title: 'Custom Home Frame',
    location: 'Frisco, TX',
    category: 'Framing',
    year: '2023',
    image: 'https://readdy.ai/api/search-image?query=custom%20new%20home%20framing%20construction%20in%20Frisco%20Texas%2C%20detailed%20wood%20framing%20with%20roof%20trusses%2C%20sunny%20day%2C%20professional%20construction%20photography%2C%20warm%20tones%2C%20showing%20craftsmanship%20and%20precision&width=600&height=450&seq=proj004&orientation=landscape',
  },
  {
    title: 'Decorative Concrete Driveway',
    location: 'Arlington, TX',
    category: 'Concrete',
    year: '2023',
    image: 'https://readdy.ai/api/search-image?query=beautiful%20decorative%20stamped%20concrete%20driveway%20and%20patio%20in%20Arlington%20Texas%20home%2C%20elegant%20pattern%2C%20warm%20earth%20tones%2C%20professional%20landscaping%20around%20it%2C%20sunny%20day%2C%20wide%20angle%20view&width=600&height=450&seq=proj005&orientation=landscape',
  },
  {
    title: 'Master Bath Remodel',
    location: 'McKinney, TX',
    category: 'Remodeling',
    year: '2023',
    image: 'https://readdy.ai/api/search-image?query=luxurious%20master%20bathroom%20remodel%20completed%20in%20McKinney%20Texas%2C%20large%20walk-in%20shower%20with%20tile%2C%20freestanding%20tub%2C%20double%20vanity%2C%20warm%20lighting%2C%20professional%20interior%20photography%2C%20elegant%20and%20modern%20design&width=600&height=450&seq=proj006&orientation=landscape',
  },
];

export default function ProjectsSection() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? projects
    : projects.filter((p) => p.category === activeCategory);

  return (
    <section id="projects" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span className="text-orange-500 text-sm font-bold tracking-widest uppercase">Our Portfolio</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Featured <span className="text-orange-500">Projects</span>
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Real work, real results. Browse our completed projects across the Dallas-Fort Worth metroplex.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center justify-center gap-2 mb-12 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-orange-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project) => (
            <div
              key={project.title}
              className="group relative rounded-2xl overflow-hidden cursor-pointer"
            >
              <div className="w-full h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity"></div>

              {/* Badge */}
              <div className="absolute top-4 right-4 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                {project.year}
              </div>
              <div className="absolute top-4 left-4 bg-black/40 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">
                {project.category}
              </div>

              {/* Info */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="text-white font-black text-lg leading-tight">{project.title}</h3>
                <div className="flex items-center gap-1 mt-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line text-orange-400 text-xs"></i>
                  </div>
                  <span className="text-gray-300 text-xs">{project.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-orange-500 text-white font-bold px-8 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            Start Your Project Today
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
